import { useMutation } from '@tanstack/react-query';
import { useId, useMemo, useState } from 'react';

import { BadgeCell } from '@/components/table/cells';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/lib/auth/auth-context';

import { MethodChip } from './MethodChip';
import { sendConsoleRequest, type ConsoleResponse } from './api-console-query';
import { buildPath, missingPathParameters, type ApiOperation } from './operations';

interface RequestPanelProps {
  readonly operation: ApiOperation;
}

/**
 * One operation's parameters, and the button that sends it.
 *
 * Keyed on the operation id by the caller, so switching endpoints resets the
 * form rather than carrying one endpoint's `id` into another's — which would be
 * a request nobody meant to make.
 */
export function RequestPanel({ operation }: RequestPanelProps) {
  const { api } = useAuth();
  const bodyId = useId();

  const [values, setValues] = useState<Record<string, string>>({});
  const [body, setBody] = useState('');
  const [bodyError, setBodyError] = useState<string | null>(null);
  const [result, setResult] = useState<ConsoleResponse | null>(null);

  const pathParameters = operation.parameters.filter((parameter) => parameter.in === 'path');
  const queryParameters = operation.parameters.filter((parameter) => parameter.in === 'query');

  const resolvedPath = useMemo(() => buildPath(operation.path, values), [operation.path, values]);
  const missing = missingPathParameters(operation, values);

  const send = useMutation({
    mutationFn: async (): Promise<ConsoleResponse> => {
      let parsedBody: unknown;
      if (operation.hasRequestBody && body.trim() !== '') {
        // Parsed here rather than sent raw: a malformed body should fail with
        // "that is not JSON", not as a 400 from the API that reads like the
        // endpoint refused it.
        parsedBody = JSON.parse(body);
      }

      return sendConsoleRequest(api, {
        method: methodOf(operation),
        // The client prefixes /api/v1; the document's paths already carry it.
        path: resolvedPath.replace(/^\/api\/v1/, ''),
        query: Object.fromEntries(
          queryParameters
            .map((parameter) => [parameter.name, values[parameter.name] ?? ''] as const)
            .filter(([, value]) => value !== ''),
        ),
        body: parsedBody,
      });
    },
    onMutate: () => {
      setBodyError(null);
      setResult(null);
    },
    onSuccess: (response) => {
      setResult(response);
    },
    onError: (error: Error) => {
      if (error instanceof SyntaxError) setBodyError('The request body is not valid JSON.');
    },
    retry: false,
  });

  return (
    <section
      aria-label={`${operation.method.toUpperCase()} ${operation.path}`}
      className="flex flex-col overflow-hidden rounded-xl border border-border bg-card"
    >
      <div className="flex items-center gap-2.5 px-[18px] pt-3.5 pb-3">
        <MethodChip method={operation.method} />
        <span className="min-w-0 font-mono text-[13px] break-all text-foreground">{resolvedPath}</span>
        <span className="ml-auto shrink-0">
          <BadgeCell label={operation.requiresAuth ? 'sent as you' : 'public'} tone="mute" />
        </span>
      </div>

      <div className="flex flex-col gap-4 border-t border-border px-[18px] pt-3.5 pb-4">
        {operation.description === undefined ? null : (
          <p className="text-[13px] leading-relaxed text-muted-foreground">{operation.description}</p>
        )}

        {operation.parameters.length === 0 ? null : (
          <div className="flex flex-col gap-3">
            {[...pathParameters, ...queryParameters].map((parameter) => (
              <ParameterField
                key={`${parameter.in}:${parameter.name}`}
                name={parameter.name}
                location={parameter.in}
                required={parameter.required}
                description={parameter.description}
                value={values[parameter.name] ?? ''}
                onChange={(value) => {
                  setValues((current) => ({ ...current, [parameter.name]: value }));
                }}
              />
            ))}
          </div>
        )}

        {operation.hasRequestBody ? (
          <div className="flex flex-col gap-2">
            <Label htmlFor={bodyId} className="text-[12.5px] font-medium text-muted-foreground">
              Body
            </Label>
            <Textarea
              id={bodyId}
              value={body}
              rows={6}
              spellCheck={false}
              onChange={(event) => {
                setBody(event.target.value);
              }}
              className="font-mono text-[13px]"
            />
          </div>
        ) : null}

        <div className="flex items-center gap-3">
          <Button
            disabled={missing.length > 0 || send.isPending}
            onClick={() => {
              send.mutate();
            }}
            title={missing.length === 0 ? undefined : `Fill in ${missing.join(', ')} first.`}
          >
            {send.isPending ? 'Sending…' : 'Send'}
          </Button>
          {missing.length > 0 ? (
            <span className="text-[13px] text-muted-foreground">Fill in {missing.join(', ')} first.</span>
          ) : null}
          {result === null ? null : (
            <span className="font-mono text-[13px] text-muted-foreground">{result.durationMs}ms</span>
          )}
        </div>

        {bodyError === null ? null : (
          <Alert role="alert" tone="err">
            <AlertDescription>{bodyError}</AlertDescription>
          </Alert>
        )}
        {send.isError && bodyError === null ? (
          <Alert role="alert" tone="err">
            <AlertDescription>{send.error.message}</AlertDescription>
          </Alert>
        ) : null}

        {result === null ? null : (
          <pre
            aria-label="Response"
            className="max-h-[360px] overflow-auto rounded-md bg-muted p-3 font-mono text-xs leading-relaxed text-foreground"
          >
            {JSON.stringify(result.data, null, 2)}
          </pre>
        )}
      </div>
    </section>
  );
}

interface ParameterFieldProps {
  readonly name: string;
  readonly location: string;
  readonly required: boolean;
  readonly description: string | undefined;
  readonly value: string;
  readonly onChange: (value: string) => void;
}

function ParameterField({ name, location, required, description, value, onChange }: ParameterFieldProps) {
  const id = useId();

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id} className="text-[13px] font-normal text-muted-foreground">
        <span className="font-mono">{name}</span>
        <span className="text-xs text-muted-foreground">{location}</span>
        {required ? <span className="text-xs text-err-fg">required</span> : null}
      </Label>
      <Input
        id={id}
        value={value}
        placeholder={description ?? ''}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </div>
  );
}

/** The document's lower-case method, as the client's union. */
function methodOf(operation: ApiOperation): 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' {
  switch (operation.method) {
    case 'post':
      return 'POST';
    case 'put':
      return 'PUT';
    case 'patch':
      return 'PATCH';
    case 'delete':
      return 'DELETE';
    case 'get':
      return 'GET';
  }
}
